import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, Users, Camera, X, Sparkles, CheckCircle2, Loader2, Home, Play, Pause, SkipForward, RotateCcw, Square } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_PRESETS = [
  { label: '5 min',  minutes: 5,    emoji: '⚡', desc: 'Snabb refresh' },
  { label: '10 min', minutes: 10,   emoji: '⏱️', desc: 'Korta vinster' },
  { label: '1 tim',  minutes: 60,   emoji: '🕐', desc: 'Riktigt fix' },
  { label: '2 tim',  minutes: 120,  emoji: '🕑', desc: 'Stor städning' },
  { label: '1 dag',  minutes: 480,  emoji: '🗓️', desc: 'Hela dagen' },
];

function CompletionButton({ taskId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const complete = async (e) => {
    e?.stopPropagation();
    if (done || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cleaning/tasks/${taskId}/complete`, {}, { withCredentials: true });
      setDone(true);
      onDone?.();
    } catch {
      toast.error('Kunde inte markera klar');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={complete}
      disabled={busy}
      data-testid={`triage-done-${taskId}`}
      className={`p-1.5 rounded-full transition ${
        done ? 'bg-green-100 text-green-600' : 'bg-white text-gray-400 hover:bg-emerald-100 hover:text-emerald-600 border border-gray-200'
      }`}
      title={done ? 'Klar 💛' : 'Markera klar'}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className={`w-3.5 h-3.5 ${done ? 'fill-green-500 text-white' : ''}`} />}
    </button>
  );
}

// =============== 🚀 LIVE SESSION RUNNER ===============
// Soft chime when a step finishes (encoded WAV beep, ~140ms)
const CHIME_DATA_URI = (() => {
  try {
    const sr = 22050;
    const dur = 0.18;
    const N = Math.floor(sr * dur);
    const buf = new ArrayBuffer(44 + N * 2);
    const dv = new DataView(buf);
    const wstr = (o, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
    wstr(0, 'RIFF'); dv.setUint32(4, 36 + N * 2, true); wstr(8, 'WAVEfmt ');
    dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
    dv.setUint32(24, sr, true); dv.setUint32(28, sr * 2, true);
    dv.setUint16(32, 2, true); dv.setUint16(34, 16, true); wstr(36, 'data'); dv.setUint32(40, N * 2, true);
    for (let i = 0; i < N; i++) {
      const t = i / sr;
      const env = Math.exp(-t * 12);
      const s = Math.sin(2 * Math.PI * 880 * t) * env * 0.55;
      dv.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s)) * 32767, true);
    }
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return 'data:audio/wav;base64,' + btoa(bin);
  } catch { return null; }
})();

function fmt(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function CleanSessionRunner({ totalMinutes, steps, onExit, onStepComplete }) {
  // steps = [{ id, title, minutes, points }]
  const [idx, setIdx] = useState(0);
  const [running, setRunning] = useState(true);
  const [done, setDone] = useState(() => new Set());
  const [stepElapsed, setStepElapsed] = useState(0); // seconds inside current step
  const [totalElapsed, setTotalElapsed] = useState(0);
  const tickRef = useRef(null);
  const chimeRef = useRef(null);

  const totalSec = totalMinutes * 60;
  const curStep = steps[idx];
  const curStepSec = (curStep?.minutes || 1) * 60;
  const stepLeft = Math.max(0, curStepSec - stepElapsed);
  const totalLeft = Math.max(0, totalSec - totalElapsed);
  const totalPct = Math.min(100, (totalElapsed / totalSec) * 100);
  const stepPct = Math.min(100, (stepElapsed / curStepSec) * 100);

  // Single ticking interval — both step + total advance together
  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setStepElapsed((s) => s + 1);
      setTotalElapsed((t) => t + 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  // Auto-advance + chime when step timer hits zero
  useEffect(() => {
    if (stepElapsed >= curStepSec && curStep) {
      try { chimeRef.current?.play(); } catch {/**/}
      // Auto-move to next step but DON'T auto-mark done (user does that)
      if (idx < steps.length - 1) {
        setIdx((i) => i + 1);
        setStepElapsed(0);
      } else {
        setRunning(false);
      }
    }
  }, [stepElapsed, curStepSec, curStep, idx, steps.length]);

  const toggleDone = async (stepId) => {
    if (done.has(stepId)) return; // one-way for now
    const next = new Set(done);
    next.add(stepId);
    setDone(next);
    try { chimeRef.current?.play(); } catch {/**/}
    try {
      await axios.post(`${API}/cleaning/tasks/${stepId}/complete`, {}, { withCredentials: true });
      onStepComplete?.();
    } catch {/* silent */}
  };

  const jumpToNext = () => {
    if (idx < steps.length - 1) {
      setIdx((i) => i + 1);
      setStepElapsed(0);
    } else {
      setRunning(false);
    }
  };

  if (!steps.length) {
    return <p className="text-center text-emerald-600 py-6 text-sm">Inga steg att köra — välj tid först</p>;
  }

  const sessionEnded = totalElapsed >= totalSec || (idx === steps.length - 1 && stepElapsed >= curStepSec);

  return (
    <div className="space-y-3" data-testid="clean-session-runner">
      {CHIME_DATA_URI && <audio ref={chimeRef} src={CHIME_DATA_URI} preload="auto" />}

      {/* TOTAL timer */}
      <div className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-3 shadow-lg" data-testid="clean-total-timer">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-100">Total tid kvar</p>
            <p className="font-mono text-3xl font-black tabular-nums" data-testid="clean-total-left">{fmt(totalLeft)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-100">Av {totalMinutes} min</p>
            <p className="text-sm font-bold">{done.size}/{steps.length} klara</p>
          </div>
        </div>
        <div className="h-2 bg-white/30 rounded-full overflow-hidden mt-2">
          <div className="h-full bg-white transition-all" style={{ width: `${totalPct}%` }} />
        </div>
      </div>

      {/* CURRENT step timer */}
      {!sessionEnded && curStep && (
        <div className="rounded-2xl bg-white border-2 border-emerald-400 p-3 shadow" data-testid="clean-current-step">
          <div className="flex items-baseline justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-700">
                Steg {idx + 1}/{steps.length} · {curStep.minutes} min
              </p>
              <p className="font-bold text-emerald-900 text-base leading-tight" data-testid="clean-step-title">{curStep.title}</p>
            </div>
            <p className="font-mono text-2xl font-black tabular-nums text-emerald-700" data-testid="clean-step-left">{fmt(stepLeft)}</p>
          </div>
          <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${stepPct}%` }} />
          </div>
          <div className="flex gap-1.5 mt-2.5">
            <button
              onClick={() => toggleDone(curStep.id)}
              disabled={done.has(curStep.id)}
              data-testid="clean-mark-done"
              className={`flex-1 py-2 rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 transition ${
                done.has(curStep.id)
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" /> {done.has(curStep.id) ? 'Klar ✓' : 'Klar!'}
            </button>
            <button
              onClick={() => setRunning((r) => !r)}
              data-testid="clean-pause-toggle"
              className="px-3 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xs font-bold flex items-center gap-1"
            >
              {running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={jumpToNext}
              data-testid="clean-skip"
              className="px-3 py-2 rounded-xl bg-white border-2 border-emerald-300 text-emerald-700 text-xs font-bold flex items-center gap-1"
              title="Hoppa till nästa"
            >
              <SkipForward className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {sessionEnded && (
        <div className="rounded-2xl bg-gradient-to-r from-amber-400 to-pink-400 text-white p-4 text-center" data-testid="clean-session-end">
          <p className="text-2xl mb-1">🎉</p>
          <p className="font-extrabold text-lg">Tiden är ute!</p>
          <p className="text-sm italic">Du fick {done.size} av {steps.length} klara · {totalMinutes} min — bra jobbat 💜</p>
        </div>
      )}

      {/* Step list with strike-through */}
      <ol className="space-y-1.5" data-testid="clean-step-list">
        {steps.map((s, i) => {
          const isDone = done.has(s.id);
          const isCurrent = i === idx && !sessionEnded;
          return (
            <li
              key={s.id}
              data-testid={`clean-step-${i}`}
              className={`rounded-xl px-3 py-2 flex items-center gap-2 text-sm transition ${
                isCurrent ? 'bg-emerald-50 border-2 border-emerald-300' : 'bg-white border border-gray-200'
              } ${isDone ? 'opacity-60' : ''}`}
            >
              <button
                onClick={() => toggleDone(s.id)}
                disabled={isDone}
                data-testid={`clean-step-toggle-${i}`}
                className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition ${
                  isDone
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white border-gray-300 hover:border-emerald-400'
                }`}
                aria-label={isDone ? 'Klar' : 'Markera klar'}
              >
                {isDone && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />}
              </button>
              <span className={`flex-1 ${isDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>{s.title}</span>
              <span className="text-[10px] text-gray-400 tabular-nums">{s.minutes}m</span>
            </li>
          );
        })}
      </ol>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onExit}
          data-testid="clean-session-exit"
          className="flex-1 py-2.5 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold flex items-center justify-center gap-1.5"
        >
          <Square className="w-3.5 h-3.5" /> Avsluta session
        </button>
      </div>
    </div>
  );
}

// =============== TIME BUDGET VIEW ===============
function TimeBudgetView({ initialMinutes = 10, onRefresh }) {
  const [minutes, setMinutes] = useState(initialMinutes);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cleaning-triage/time-budget?minutes=${m}`, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error('Kunde inte hämta förslag');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(minutes); }, [minutes, load]);

  // Flatten zones → ordered list of steps for the runner
  const buildSteps = () => {
    if (!data?.zones) return [];
    const out = [];
    for (const z of data.zones) {
      for (const t of (z.tasks || [])) {
        out.push({ id: t.id, title: `${z.emoji} ${t.title}`, minutes: t.minutes, points: t.points });
      }
    }
    return out;
  };

  if (running) {
    return (
      <CleanSessionRunner
        totalMinutes={minutes}
        steps={buildSteps()}
        onExit={() => { setRunning(false); load(minutes); onRefresh?.(); }}
        onStepComplete={() => onRefresh?.()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700/70 mb-2">
          Hur mycket tid har du just nu?
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {TIME_PRESETS.map((p) => (
            <button
              key={p.minutes}
              onClick={() => setMinutes(p.minutes)}
              data-testid={`triage-time-${p.minutes}`}
              className={`py-2.5 px-2 rounded-2xl text-xs font-bold transition text-center ${
                minutes === p.minutes
                  ? 'bg-emerald-500 text-white shadow-md scale-105'
                  : 'bg-white text-emerald-700 hover:bg-emerald-50 border-2 border-emerald-100'
              }`}
            >
              <div className="text-base mb-0.5">{p.emoji}</div>
              <div>{p.label}</div>
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-center text-emerald-500 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

      {!loading && data && (
        <div className="space-y-3" data-testid="triage-time-result">
          <div className="bg-white/70 rounded-2xl p-3 text-center border border-emerald-200">
            <p className="text-xs text-emerald-600 italic">
              {data.total_tasks > 0 ? (
                <>Din lista just nu — <strong>{data.total_tasks} saker · {data.total_minutes} min · +{data.total_points} poäng</strong></>
              ) : (
                <>Inga pågående städ-tasks just nu — du har gjort allt 🎉</>
              )}
            </p>
            {data.pending_total_count > data.total_tasks && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                ({data.pending_total_count - data.total_tasks} saker till finns, men de ryms inte i tiden — det räcker med det här)
              </p>
            )}
          </div>

          {data.total_tasks > 0 && (
            <button
              onClick={() => setRunning(true)}
              data-testid="clean-start-session"
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-extrabold text-sm shadow-lg flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4 fill-white" /> Starta klockan ({minutes} min)
            </button>
          )}

          {data.zones.map((z) => (
            <div key={z.zone} className="bg-white rounded-2xl border border-emerald-100 overflow-hidden shadow-sm">
              <div className="px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between border-b border-emerald-100">
                <h3 className="font-bold text-emerald-800 text-sm flex items-center gap-1.5">
                  <span className="text-base">{z.emoji}</span> {z.zone}
                </h3>
                <span className="text-[10px] text-emerald-600 font-semibold">{z.minutes} min</span>
              </div>
              <div className="divide-y divide-gray-50">
                {z.tasks.map((t) => (
                  <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-emerald-50/50" data-testid={`triage-task-${t.id}`}>
                    <span className="text-xs text-gray-400 w-9 tabular-nums">{t.minutes}m</span>
                    <span className="flex-1 text-gray-700">{t.title}</span>
                    <span className="text-[10px] text-amber-600 font-bold">+{t.points}p</span>
                    <CompletionButton taskId={t.id} onDone={() => { load(minutes); onRefresh?.(); }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============== VISIT PLAN VIEW ===============
function VisitPlanView({ onRefresh }) {
  const [days, setDays] = useState(3);
  const [perDay, setPerDay] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/cleaning-triage/visit-plan`, {
        days, minutes_per_day: perDay,
      }, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error('Kunde inte planera');
    } finally {
      setLoading(false);
    }
  }, [days, perDay]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-emerald-700/70">
            Om hur många dagar?
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="range" min="1" max="7" value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              data-testid="triage-visit-days"
              className="flex-1 accent-emerald-500"
            />
            <span className="font-bold text-emerald-700 text-lg w-8 text-center">{days}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-emerald-700/70">
            Min per dag?
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="range" min="10" max="120" step="10" value={perDay}
              onChange={(e) => setPerDay(parseInt(e.target.value))}
              data-testid="triage-visit-perday"
              className="flex-1 accent-emerald-500"
            />
            <span className="font-bold text-emerald-700 text-lg w-10 text-center">{perDay}m</span>
          </div>
        </div>
      </div>

      {loading && <div className="text-center text-emerald-500 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}

      {!loading && data && (
        <div className="space-y-3" data-testid="triage-visit-result">
          {data.plan.map((dp) => (
            <div
              key={dp.day_index}
              className={`rounded-2xl border-2 overflow-hidden shadow-sm ${
                dp.is_visit_day
                  ? 'bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200'
                  : 'bg-white border-emerald-100'
              }`}
            >
              <div className={`px-4 py-2.5 flex items-center justify-between border-b ${
                dp.is_visit_day ? 'bg-gradient-to-r from-pink-100 to-rose-100 border-pink-200' : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-100'
              }`}>
                <h3 className={`font-bold text-sm flex items-center gap-1.5 ${dp.is_visit_day ? 'text-pink-800' : 'text-emerald-800'}`}>
                  {dp.is_visit_day && <Sparkles className="w-4 h-4" />}
                  {dp.label}
                </h3>
                <span className={`text-[11px] font-bold ${dp.is_visit_day ? 'text-pink-700' : 'text-emerald-700'}`}>
                  {dp.minutes} / {dp.max_minutes} min · {dp.tasks.length} st
                </span>
              </div>
              {dp.tasks.length === 0 && (
                <p className="text-xs text-gray-400 italic px-4 py-3">Inget planerat denna dag — det räcker.</p>
              )}
              {dp.zones && dp.zones.map((z) => (
                <div key={z.zone} className="px-3 py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase mb-1">
                    <span className="text-sm">{z.emoji}</span> {z.zone} <span className="text-gray-400 font-normal">· {z.minutes} min</span>
                  </div>
                  {z.tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 py-1 text-sm" data-testid={`triage-visit-task-${t.id}`}>
                      <span className="text-xs text-gray-400 w-8 tabular-nums">{t.minutes}m</span>
                      <span className="flex-1 text-gray-700">{t.title}</span>
                      <CompletionButton taskId={t.id} onDone={() => { load(); onRefresh?.(); }} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
          {data.skipped_count > 0 && (
            <p className="text-[11px] text-gray-400 text-center italic px-4">
              {data.skipped_count} extra saker fick inte plats — höj "min per dag" eller låt dem vara, det räcker så här 💛
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// =============== PHOTO VIEW (links to Mitt Rum) ===============
function PhotoView({ onJumpToRooms }) {
  return (
    <div className="space-y-4 text-center py-3">
      <div className="text-5xl">📸</div>
      <h3 className="text-lg font-bold text-emerald-800">Fota & få förslag</h3>
      <p className="text-sm text-emerald-600/80 italic px-4">
        Ladda upp en bild av rummet — AI:n tittar med <strong>utomstående ögon</strong> och säger exakt vad som ger störst skillnad just där.
        <br />
        Den ser saker du vant dig vid 💛
      </p>
      <button
        onClick={onJumpToRooms}
        data-testid="triage-jump-rooms"
        className="px-5 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold text-sm shadow-lg inline-flex items-center gap-2"
      >
        <Home className="w-4 h-4" /> Öppna Mitt Rum
      </button>
      <p className="text-[11px] text-gray-400 italic">
        (Du hittar samma flik högt upp under "Städ"-gruppen)
      </p>
    </div>
  );
}

// =============== MAIN MODAL ===============
export default function CleaningTriageModal({ open, onClose, onJumpToRooms }) {
  const [tab, setTab] = useState('time');

  if (!open) return null;

  const tabs = [
    { id: 'time',  label: 'Hur mycket tid?', emoji: '⏱️', icon: Clock },
    { id: 'visit', label: 'Vi får besök',     emoji: '🎉', icon: Users },
    { id: 'photo', label: 'Fota & fixa',      emoji: '📸', icon: Camera },
  ];

  return (
    <div className="fixed inset-0 z-[295] bg-emerald-900/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto" data-testid="cleaning-triage-modal">
      <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-3xl max-w-xl w-full shadow-2xl border border-emerald-200 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 px-5 py-3.5 rounded-t-3xl flex items-center justify-between border-b border-emerald-200">
          <div>
            <h1 className="text-base sm:text-lg font-bold text-emerald-800 flex items-center gap-2">
              🧹 Snabb-städ
            </h1>
            <p className="text-[11px] text-emerald-600/70 italic">
              Bara så mycket som ryms — aldrig mer
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="triage-close"
            className="p-2 rounded-full hover:bg-white/60 text-emerald-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-3 pt-3 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`triage-tab-${t.id}`}
              className={`flex-1 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                tab === t.id
                  ? 'bg-emerald-500 text-white shadow'
                  : 'bg-white text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
              }`}
            >
              <span className="text-sm">{t.emoji}</span>
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-5">
          {tab === 'time'  && <TimeBudgetView initialMinutes={10} />}
          {tab === 'visit' && <VisitPlanView />}
          {tab === 'photo' && <PhotoView onJumpToRooms={() => { onJumpToRooms?.(); onClose(); }} />}
        </div>
      </div>
    </div>
  );
}
